import React, { useState } from "react";
import styles from "./reset-password-first-login.module.css";
import { Controller, useForm } from "react-hook-form";
import CustomTextfield from "../../../common/input/custom-textfield/custom-textfield";
import { Spin } from "antd";
import CustomButton from "../../../common/custom-button/custom-button";
import { changePassword } from "../../../services/auth";
import { toast } from "react-toastify";
import { EyeInvisibleOutlined, EyeTwoTone } from "@ant-design/icons";
import useLogout from "../../../hooks/useLogout";
import PasswordRules from "../../../components/password-rules/password-rules";
import { PASSWORD_REGEX } from "../../../constants/common";

const ResetPasswordFirstLogin = () => {
  const logout = useLogout();
  const [loading, setLoading] = useState(false);
  const [passwordVisibility, setPasswordVisibility] = useState({
    new: false,
    confirm: false,
  });
  const { handleSubmit, control, watch } = useForm({
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      new_password: "",
      confirm_password: "",
    },
  });
  const newPassword = watch("new_password", "");

  const togglePassword = (field) => {
    setPasswordVisibility((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const onSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = {
        new_password: values?.new_password,
      };
      await changePassword(payload);
      toast.success(
        "Password updated successfully. Please login again with your new password."
      );
      setTimeout(() => logout(), 500);
    } catch (error) {
      console.error(error);
      toast.error(error?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Spin spinning={loading} className={styles.pageLoader}>
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.headerContent}>
            <img
              src="/assets/images/logo.svg"
              alt="Logo"
              className={styles.logo}
            />

            <h2 className={styles.title}>Set a New Password</h2>

            <p className={styles.subtitle}>
              For security reasons,<br/>
              you must change your default password before continuing.
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)}>
            <Controller
              name="new_password"
              control={control}
              rules={{
                required: true,
                minLength: {
                  value: 8,
                  message: "Minimum 8 characters required",
                },
                pattern: {
                  value: PASSWORD_REGEX,
                  message: "Must include uppercase, lowercase, number & special character",
                },
              }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <CustomTextfield
                  id="new_password"
                  label="New Password"
                  showRequired
                  labelColor="#434343"
                  type={passwordVisibility.new ? "text" : "password"}
                  suffix={
                    passwordVisibility.new ? (
                      <EyeTwoTone onClick={() => togglePassword("new")} />
                    ) : (
                      <EyeInvisibleOutlined onClick={() => togglePassword("new")} />
                    )
                  }
                  placeholder="e.g. Abc@1234"
                  size="large"
                  value={value}
                  onChange={onChange}
                  status={error ? "error" : ""}
                  errorMessage={error?.message}
                />
              )}
            />

            <Controller
              name="confirm_password"
              control={control}
              rules={{
                required: true,
                validate: (value) =>
                  value === newPassword || "Passwords do not match",
              }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <CustomTextfield
                  id="confirm_password"
                  label="Confirm Password"
                  showRequired
                  labelColor="#434343"
                  type={passwordVisibility.confirm ? "text" : "password"}
                  suffix={
                    passwordVisibility.confirm ? (
                      <EyeTwoTone onClick={() => togglePassword("confirm")} />
                    ) : (
                      <EyeInvisibleOutlined onClick={() => togglePassword("confirm")} />
                    )
                  }
                  placeholder="Confirm your password"
                  size="large"
                  value={value}
                  onChange={onChange}
                  status={error ? "error" : ""}
                  errorMessage={error?.message}
                />
              )}
            />
            <PasswordRules password={newPassword} />
            <CustomButton
              title="Update Password"
              type="primary"
              htmlType="submit"
              className={styles.button}
            />
          </form>
        </div>
      </div>
    </Spin>
  );
};

export default ResetPasswordFirstLogin;